import { useMemo } from 'react';
import { Calculation } from '@carbon/react/icons';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card';
import { UatNriMatrixCard } from 'components/uat-nri-matrix-card';
import type { CalculationType, SubtractNRI } from 'components/nri-table/types';
import {
  DECIMAL_VARIABLE,
  EMPTY,
  NESTED_TABLE_MULTISELECT_CONFIG,
  PERCENT_2,
} from './utils';
import { useInertChildSelection } from './hooks';
import styles from './subtract-nri-table.module.scss';

type SubtractTableRow = SubtractNRI & Record<string, unknown>;

type SubtractColumnKey = keyof SubtractNRI & string;

/**
 * Variable-precision numeric column (matches the source `formatVariable`).
 *
 * @param {string} accessorKey - The `SubtractNRI` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function variable(
  accessorKey: SubtractColumnKey,
  header: string,
  size: number
): DataTableColumnDef<SubtractTableRow> {
  return { accessorKey, header, size, meta: DECIMAL_VARIABLE };
}

/**
 * 2-dp percentage column (matches the source `formatPercentage`).
 *
 * @param {string} accessorKey - The `SubtractNRI` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function percent(
  accessorKey: SubtractColumnKey,
  header: string,
  size: number
): DataTableColumnDef<SubtractTableRow> {
  return { accessorKey, header, size, meta: PERCENT_2 };
}

/**
 * Plain-text column.
 *
 * @param {string} accessorKey - The `SubtractNRI` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function text(
  accessorKey: SubtractColumnKey,
  header: string,
  size: number
): DataTableColumnDef<SubtractTableRow> {
  return { accessorKey, header, size, meta: { type: 'text' } };
}

/**
 * The "Show Calculation" trigger icon shared by the unit-interest popups.
 *
 * @param {boolean} active - Whether the popover is open.
 * @param {Function} toggle - Toggles the popover.
 * @return {JSX.Element} The trigger icon button.
 */
const renderCalculationTrigger = (active: boolean, toggle: () => void) => (
  <CalculationIconTrigger
    definition="Show Calculation"
    active={active}
    onClick={toggle}
  >
    <Calculation aria-hidden="true" role="img" />
  </CalculationIconTrigger>
);

/**
 * UAT NRI / UAT WI cell: the value with a precision tooltip, plus the two reused
 * formula popups — the unit-interest calculation (`LazyUnitInterestCard`) and the
 * NRI/WI matrix (`UatNriMatrixCard`), both keyed by `nriCalculationId` and
 * fetching their own data. Verification controls are omitted in eval3-new (per
 * spec). Mirrors the legacy + interest-certification UAT cell.
 *
 * @param {number | null} value - The UAT NRI or WI value.
 * @param {number} nriCalculationId - The subtract's NRI calculation id (keys both popups).
 * @param {CalculationType} calculationType - 'NRI' or 'WI'.
 * @return {JSX.Element} The UAT cell with value + formula popups.
 */
function uatCellRenderer(
  value: number | null,
  nriCalculationId: number,
  calculationType: CalculationType
) {
  return (
    <span className={styles.uatCell}>
      {formatNumberWithTooltip(value, 8)}
      {/* Ternary (not `&&`) so a falsy `nriCalculationId` (the adapter sets 0
          when the BE omits it) renders nothing rather than leaking a literal 0. */}
      {nriCalculationId ? (
        <>
          <LazyUnitInterestCard
            nriCalculationId={nriCalculationId}
            unitInterestValue={value}
            calculationType={calculationType}
            renderTrigger={renderCalculationTrigger}
          />
          <UatNriMatrixCard
            nriCalculationId={nriCalculationId}
            calculationType={calculationType}
          />
        </>
      ) : null}
    </span>
  );
}

const SUBTRACT_NRI_COLUMNS: DataTableColumnDef<SubtractTableRow>[] = [
  {
    accessorKey: 'crmSubtractId',
    header: 'CRM Subtract',
    size: 200,
    cell: (info) => {
      const value = info.getValue<string>();
      const { crmSubtractSalesforceId } = info.row.original;
      if (!value || !crmSubtractSalesforceId) {
        return value || EMPTY;
      }
      return (
        <Link
          to={`/crm/tracts_and_subtracts/${encodeURIComponent(
            crmSubtractSalesforceId
          )}/edit`}
          openInNewTab
        >
          {value}
        </Link>
      );
    },
  },
  text('uat', 'UAT', 160),
  {
    accessorKey: 'uatNri',
    header: 'UAT NRI',
    size: 240,
    meta: { type: 'decimal', wrap: true },
    cell: (info) =>
      uatCellRenderer(
        info.getValue<number | null>(),
        info.row.original.nriCalculationId,
        'NRI'
      ),
  },
  {
    accessorKey: 'uatWi',
    header: 'UAT WI',
    size: 240,
    meta: { type: 'decimal', wrap: true },
    cell: (info) =>
      uatCellRenderer(
        info.getValue<number | null>(),
        info.row.original.nriCalculationId,
        'WI'
      ),
  },
  variable('subtractNetAcres', 'Subtract Net Acres', 170),
  variable('subtractGrossAcres', 'Subtract Gross Acres', 180),
  text('quarterCall', 'Quarter Call', 150),
  variable('partTractFactor', 'Part. Tract Factor', 160),
  variable('partTractAcres', 'Part. Tract Acres', 160),
  variable('leaseRoyaltyRate', 'Lease Royalty Rate', 170),
  text('leaseRoyaltyType', 'Lease Royalty Type', 170),
  percent('ptfAdjustment', 'PTF Adjustment', 150),
  percent('unitCoverage', 'Unit Coverage', 140),
  percent('uatCoverage', 'UAT Coverage', 140),
  percent('focusMdac', 'Focus - MDAC', 140),
  text('executive', 'Executive', 160),
  variable('bonus', 'Bonus', 130),
  variable('delay', 'Delay', 130),
  variable('royalty', 'Royalty', 130),
  variable('royaltyBurdens', 'Royalty Burdens', 160),
  variable('wi', 'WI', 130),
  variable('orri', 'ORRI', 130),
  variable('orriBurdens', 'ORRI Burdens', 150),
  text('interestType', 'Interest Type', 150),
];

interface SubtractNriTableProps {
  subtracts: SubtractNRI[];
  /**
   * The parent (slot-allocation) row id — the subtract checkboxes follow its pin
   * state for visual parity (map-inert, so they scope nothing).
   */
  parentRowId: string;
}

/**
 * Subtract-NRI sub-table, ported from `components/nri-table`'s
 * `DEFAULT_SUBTRACT_COLUMNS` onto a stripped-down nested `<DataTable>`. Shared by
 * the Slot and Slot Allocation expanded rows. UAT NRI / WI show the value with a
 * precision tooltip; the verification system is removed in P1.
 *
 * @param {SubtractNriTableProps} props - The subtract-NRI rows to render.
 * @return {JSX.Element} The nested subtract-NRI table.
 */
export function SubtractNriTable({
  subtracts,
  parentRowId,
}: SubtractNriTableProps) {
  // Multi-select for parity with the geometry subtables, but presentational only —
  // subtract names match no map feature, so these checkboxes scope nothing. They
  // follow the parent row's pin state: checked while pinned, cleared on unpin.
  const childIds = useMemo(
    () =>
      (subtracts as SubtractTableRow[]).map((subtract) => String(subtract.id)),
    [subtracts]
  );
  const { selection, onSelectionChange } = useInertChildSelection(
    parentRowId,
    childIds
  );

  return (
    <DataTable<SubtractTableRow>
      data={subtracts as SubtractTableRow[]}
      columns={SUBTRACT_NRI_COLUMNS}
      getRowId={(row) => String(row.id)}
      config={NESTED_TABLE_MULTISELECT_CONFIG}
      controlledState={{ selection }}
      onSelectionChange={onSelectionChange}
    />
  );
}
